// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { merge, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { LoadingStatus, Loaded, Policy, Loadable, QueueAlertAction, AlertType, GoAction } from '@app/core';
import { State, RemovePolicyByIdAction } from '../../state';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { DataTableAjaxComponent, SelectedRows } from '@app/shared/data-table-ajax/data-table-ajax.component';
import { PoliciesFacadeService } from '@app/access-management/state/policies.facade';
import { RemovePolicyModalComponent, RemoveResult } from './remove-policy-modal/remove-policy-modal.component';
import { SelectPolicyAction } from '@app/access-management/state/policy-details/policy-details.actions';

@Component({
  selector: 'app-policies-list',
  templateUrl: './policies-list.component.html',
  styleUrls: ['./policies-list.component.scss']
})
export class PoliciesListComponent implements OnInit, AfterViewInit {
  @ViewChild('inUseColumnTemplate') inUseColumnTemplate: any;
  @ViewChild('actionColumnTemplate') actionColumnTemplate: any;
  @ViewChild('statementActions') statementActions: any;
  @ViewChild('dataTableAjax') dataTableAjax: DataTableAjaxComponent | undefined;
  columns: any[] = [];

  readonly policies$: Observable<Loaded<Policy[]> | Loadable>;
  LOAD_COMPLETE = LoadingStatus.LOAD_COMPLETE;

  currentPageNumber = 0;
  filteredList$ = new Subject();
  tableList$: any;
  fullList = [];

  statementsColumns: any[] = [];

  constructor(
    private store: Store<State>,
    private policiesFacade: PoliciesFacadeService,
    private modalService: NgbModal
  ) {
    this.policiesFacade.initialize();
    this.policies$ = this.policiesFacade.policies$;
  }

  ngAfterViewInit() {
    this.loadTableColumns();
  }

  loadTableColumns() {
    this.columns = [
      {
        name: 'Policy Name',
        prop: 'name',
        cellClass: 'text-info',
        flexGrow: 1
      },
      {
        name: 'Usage',
        prop: 'numUsers',
        cellClass: 'text-info',
        flexGrow: 1,
        sortable: false,
        canAutoResize: false,
        draggable: false,
        resizeable: false,
        cellTemplate: this.inUseColumnTemplate
      },
      {
        name: 'Type',
        prop: 'type',
        flexGrow: 1
      },
      {
        name: 'Description',
        prop: 'description',
        flexGrow: 2
      },
      {
        name: 'Actions',
        prop: 'id',
        cellTemplate: this.actionColumnTemplate,
        flexGrow: 1
      }
    ];

    this.statementsColumns = [
      {
        name: 'Effect',
        prop: 'effect',
        flexGrow: 1
      },
      {
        name: 'Service',
        prop: 'service_name',
        flexGrow: 1
      },
      {
        name: 'Resource',
        prop: 'resource_name',
        flexGrow: 1
      },
      {
        name: 'Actions',
        prop: 'action',
        flexGrow: 1,
        cellTemplate: this.statementActions
      }
    ];
  }

  actionsFormat(actions: any) {
    return actions.map((action: any) => action.name).join(', ');
  }

  onSelectRow(rows: SelectedRows) {
    const row = rows.selected[0];
    this.store.dispatch(new SelectPolicyAction(row));
    this.store.dispatch(new GoAction(['/iam/policies/', row.id]));
  }

  onEditRow($event: Event, row: any) {
    $event.preventDefault();
    $event.stopPropagation();
    this.store.dispatch(new SelectPolicyAction(row));
    this.store.dispatch(new GoAction(['/iam/policies/', row.id, 'edit']));
  }

  handleRemoveButton($event: any, value: string) {
    $event.preventDefault();
    $event.stopPropagation();
    this.onRemoveRow(value);
  }

  onRemoveRow(removedId: string) {
    const modalRef = this.modalService.open(RemovePolicyModalComponent, { centered: true, windowClass: 'no-border' });
    modalRef.componentInstance.removedPolicyId = removedId;
    // Wait for a result from the modal. Note, this method can't use async await, because it causes funny behavior
    // when called from an angular template
    const resultPromise = modalRef.result as Promise<RemoveResult>;
    resultPromise.then(result => {
      switch (result) {
        case RemoveResult.REMOVE_SUCCEEDED: {
          // Todo: if ACS server work is done, refresh policy list will reflect new changes. [Verify need]
          this.store.dispatch(new RemovePolicyByIdAction(removedId));
          this.store.dispatch(
            new QueueAlertAction({ alertText: `Policy delete succeed.`, alertType: AlertType.SUCCESS })
          );
          if (this.dataTableAjax) {
            this.dataTableAjax.refreshTable();
          }
          break;
        }

        case RemoveResult.REMOVE_FAILED: {
          this.store.dispatch(new QueueAlertAction({ alertText: `Policy delete failed.`, alertType: AlertType.ERROR }));
          break;
        }

        default: {
          break;
        }
      }
    });
  }

  ngOnInit() {
    this.tableList$ = this.policies$.pipe(tap(policies => (this.fullList = (policies && policies.data) || [])));
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/policies/policies-list/policies-list.component.ts